import { useNavigate, useParams } from 'react-router-dom';

import { ChevronLeft } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { getMobileAppInfo, sendWebMessage } from '@lemon/app-messages';
import { Images } from '@lemon/assets';
import { useAnalyzedPouch } from '@lemon/pouches';
import { LoadingFallback } from '@lemon/shared';

export const ResultPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { data: analyzedPouch, isLoading, error } = useAnalyzedPouch({ id: id!, force: true });
    const { isOnMobileApp } = getMobileAppInfo();

    const handleBack = () => {
        if (isOnMobileApp) {
            sendWebMessage({ type: 'PopWebView' });
        } else {
            navigate(-1);
        }
    };

    if (isLoading) {
        return <LoadingFallback />;
    }

    if (error || !analyzedPouch) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">데이터를 불러오는 중 오류가 발생했습니다.</p>
                    <button onClick={handleBack} className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800">
                        뒤로 가기
                    </button>
                </div>
            </div>
        );
    }

    // Personal color code to Korean label conversion
    const getPersonalColorLabel = (code: string): string => {
        if (!code) return '기타';

        const [season, mood] = code.split('-');

        const seasonMap: Record<string, string> = {
            spring: '봄',
            summer: '여름',
            autumn: '가을',
            winter: '겨울',
        };

        const moodMap: Record<string, string> = {
            light: '라이트',
            bright: '브라이트',
            mute: '뮤트',
            deep: '딥',
            warm: '웜',
            cool: '쿨',
            dark: '다크',
        };

        const seasonKor = seasonMap[season?.toLowerCase()] || season;
        const moodKor = moodMap[mood?.toLowerCase()] || mood;

        return `${seasonKor} ${moodKor}`;
    };

    const getPersonalTypeImage = (personalType: string) => {
        switch (personalType) {
            case 'octopus':
                return Images.octopus; // 문어
            case 'chameleon':
                return Images.chameleon; // 카멜레온
            case 'lunchbox':
                return Images.lunchbox; // 도시락
            case 'cactus':
                return Images.cactus; // 선인장
            case 'pine':
                return Images.pine; // 소나무
            default:
                return Images.pine; // 기본값 (분석 진행 중)
        }
    };

    const getPersonalTypeInfo = (personalType: string) => {
        switch (personalType) {
            case 'octopus':
                return {
                    title: '호기심 천국 문어형',
                    subTitle: '가능성이 무한한 색조 탐험러',
                    description:
                        '다양한 제품을 써보며 나만의 스타일을 찾는 과정을 즐기는 타입이에요. 아직 조합이 정리되진 않았지만, 누구보다 가능성이 많고 색조 표현의 폭이 넓은 사람이에요.',
                    bg: '#F5DBDB',
                };
            case 'chameleon':
                return {
                    title: '알록달록 카멜레온형',
                    subTitle: '다양한 분위기를 소화하는 색조 플레이어',
                    description:
                        '고정된 스타일보다는 변화와 새로움을 즐기며 다양한 제품을 시도하는 타입이에요. 틀에 박힌 메이크업보다는 나에게 맞는 룩을 자유롭게 선택하는 사람이에요.',
                    bg: '#E4F5DB',
                };
            case 'lunchbox':
                return {
                    title: '조화로운 도시락형',
                    subTitle: '자신만의 감각으로 색을 조화롭게 활용하는',
                    description:
                        '여러 톤을 섞어 쓰면서 상황과 무드에 맞는 조합을 연출하는 타입이에요. 다채로운 조합 속에서도 나만의 스타일을 완성하는 사람이에요.',
                    bg: '#E0DBF5',
                };
            case 'cactus':
                return {
                    title: '킥포인트 선인장형',
                    subTitle: '기본은 지키면서 포인트는 확실하게',
                    description:
                        '주로 사용하는 톤은 정해져 있지만, 그날의 기분이나 상황에 따라 가볍게 변화를 시도하는 타입이에요. 안정성과 개성을 균형 있게 추구하는 사람이에요.',
                    bg: '#F5ECDB',
                };
            case 'pine':
                return {
                    title: '일편단심 소나무형',
                    subTitle: '흔들림 없이 한 길만 걷는',
                    description:
                        '새로움보다는 완성된 조합과 안정적인 스타일을 선호하는 타입이에요. 조합 실패가 적고, 메이크업 무드가 안정적인 사람이에요.',
                    bg: '#DEF0FF',
                };
            default:
                // personal이 null이거나 없는 경우 기본 분석 진행 중 상태로 표시
                return {
                    title: '분석 진행 중',
                    description: '파우치 분석이 진행 중입니다. 분석이 완료되면 개인 맞춤 타입을 확인하실 수 있습니다.',
                    bg: '#F8F9FA',
                };
        }
    };

    // Process tone data (descending order, only items with ratioMillis)
    const toneData =
        analyzedPouch.tone$$
            ?.filter(tone => tone.ratioMillis && tone.ratioMillis > 0)
            .sort((a, b) => (b.ratioMillis || 0) - (a.ratioMillis || 0))
            .map(tone => ({
                name: getPersonalColorLabel(tone.code || ''),
                value: (tone.ratioMillis || 0) / 10, // ratioMillis를 비율(0-100%)로 변환
                count: tone.count || 0,
                season: tone.season || '',
                code: tone.code || 'other', // 코드가 없으면 'other'로 설정
            })) || [];

    const COLORS = ['#F8B5B8', '#FBD690', '#F7C1FF', '#AEB8DD', '#DAC6A9', '#AFD09E', '#C1AAFF', '#D695A1'];

    // Process product data (using product$$ data)
    const products =
        analyzedPouch.product$$?.map(product => {
            const mapX = product.map$?.x ?? 0;
            const mapY = product.map$?.y ?? 0;

            // Coordinate transformation (-100~100 to 0~100%)
            // X-axis: -100(clear/left) ~ 100(dull/right)
            // Y-axis: -100(bright/top) ~ 100(dark/bottom) - Y-axis inversion needed
            const x = ((mapX + 100) / 200) * 100;
            const y = ((100 - mapY) / 200) * 100; // Y-axis inversion: negative is top, positive is bottom

            // Use image$ or default image if area$ is not available
            const imageUrl = product.image$?.url || product.area$?.url || Images.thumb;

            return {
                id: product.id,
                name: product.name || '제품명 없음',
                imageUrl,
                x,
                y,
                brand: product.nameOfBrand || '브랜드 없음',
                color: product.nameOfColor || '색상 없음',
                category: product.category$?.name || '카테고리 없음',
                tone: product.tone$ || {},
                toneCode: product.tone$?.code || '',
                originalMapX: mapX,
                originalMapY: mapY,
                hasArea: !!product.area$,
            };
        }) || [];

    // Group products by personal color code
    const groupProductsByToneCode = () => {
        const grouped: Record<string, typeof products> = {};

        products.forEach(product => {
            const code = product.toneCode || 'other';
            if (!grouped[code]) {
                grouped[code] = [];
            }
            grouped[code].push(product);
        });

        return grouped;
    };

    const groupedProducts = groupProductsByToneCode();

    // Sort by code (to match personal color ratio order)
    const sortedGroupKeys = Object.keys(groupedProducts).sort((a, b) => {
        if (a === 'other') return 1;
        if (b === 'other') return -1;

        // toneData 순서에 맞춰 정렬
        const aIndex = toneData.findIndex(tone => tone.code === a);
        const bIndex = toneData.findIndex(tone => tone.code === b);

        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;

        return aIndex - bIndex;
    });

    const personalTypeInfo = getPersonalTypeInfo(analyzedPouch.personal || '');

    const ColorLegend = ({ payload }) => {
        return (
            <div className="flex flex-col space-y-1">
                {payload.map((entry, index) => {
                    const dataItem = toneData.find(item => item.name === entry.value);
                    return (
                        <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center space-x-1">
                                <div
                                    className="w-[10px] h-[10px] rounded-[2px]"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm max-[320px]:text-[12px] text-[#B3B4BA]">{entry.value}</span>
                            </div>
                            <span className="text-sm max-[320px]:text-[12px] font-bold">
                                {dataItem?.value.toFixed(1)}%
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div>
            {!isOnMobileApp && (
                <header className="sticky top-0 z-50 h-[56px] bg-white flex items-center justify-between">
                    <button className="p-3 flex items-center justify-center" onClick={handleBack}>
                        <ChevronLeft strokeWidth={1} />
                    </button>
                </header>
            )}

            <section>
                <div
                    className={`${
                        isOnMobileApp ? 'h-[565px] pt-[106px] pb-[50px]' : 'h-[509px] py-[50px]'
                    } px-4 flex flex-col items-center`}
                    style={{ backgroundColor: personalTypeInfo.bg }}
                >
                    <div className="text-[20px] text-center mb-5">
                        {analyzedPouch.owner$?.nick || analyzedPouch.owner$?.name || '사용자'} 님의{' '}
                        <div className="text-[28px] font-bold">{analyzedPouch.name || '파우치'}</div>
                    </div>
                    <img src={getPersonalTypeImage(analyzedPouch.personal || '')} className="w-[225px]" alt="" />
                    <div className="w-full rounded-lg bg-white py-[30px] px-5 shadow-[0_0_12px_0_rgba(0,0,0,0.10)]">
                        <div className="text-center mb-5 text-[14px]">
                            {personalTypeInfo.subTitle}
                            <div className="text-[24px] font-bold">{personalTypeInfo.title}</div>
                        </div>
                        <p className="text-[14px] text-[#B3B4BA] text-center">{personalTypeInfo.description}</p>
                    </div>
                </div>
            </section>

            <section className="px-4 pt-[95px]">
                <div className="font-bold mb-6">퍼스널컬러 비율</div>
                {toneData.length > 0 ? (
                    <div className="flex items-center justify-center gap-6 pb-[30px] max-[320px]:gap-4">
                        <div className="flex-1">
                            <ResponsiveContainer width={160} height={160}>
                                <PieChart>
                                    <Pie
                                        data={toneData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        startAngle={90}
                                        endAngle={450}
                                        paddingAngle={0}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {toneData.map((entry, idx) => {
                                            // '기타' 항목은 그레이색으로 표시
                                            const color =
                                                entry.code === 'other' ? '#B3B4BA' : COLORS[idx % COLORS.length];
                                            return <Cell key={`cell-${idx}`} fill={color} />;
                                        })}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex-1">
                            <ColorLegend
                                payload={toneData.map((item, idx) => ({
                                    value: item.name,
                                    color: item.code === 'other' ? '#B3B4BA' : COLORS[idx % COLORS.length],
                                }))}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="text-[#B3B4BA] text-sm">퍼스널컬러 분석이 진행 중입니다.</div>
                    </div>
                )}
            </section>
            <div className="border-b border-[#F3F3F8]"></div>
            <section className="py-[30px] px-4">
                <div className="font-bold mb-6">제품별 퍼스널컬러</div>
                <div className="flex flex-col space-y-5">
                    {sortedGroupKeys.map(groupKey => {
                        const groupProducts = groupedProducts[groupKey];
                        const isOther = groupKey === 'other';
                        const colorIndex = toneData.findIndex(tone => tone.code === groupKey);
                        const groupColor = isOther ? '#B3B4BA' : COLORS[colorIndex % COLORS.length];
                        const groupLabel = isOther ? '기타' : getPersonalColorLabel(groupKey);

                        return (
                            <div key={groupKey} className="flex flex-col space-y-2">
                                <div className="flex items-center gap-1 text-sm font-bold">
                                    <div
                                        className="w-[10px] h-[10px] rounded-[2px]"
                                        style={{ backgroundColor: groupColor }}
                                    ></div>
                                    {groupLabel}
                                    <span className="ml-1 text-[#B3B4BA] font-normal">{groupProducts.length}개</span>
                                </div>
                                {groupProducts.map((product, productIndex) => (
                                    <div key={`${groupKey}-${productIndex}`} className="flex items-center gap-2">
                                        <div className="w-[60px] h-[60px] rounded-[2px] bg-[#F3F3F8] flex items-center justify-center overflow-hidden py-[7px] px-[6px] shrink-0">
                                            <img
                                                className="w-full h-full object-contain"
                                                src={product.imageUrl}
                                                alt={product.name}
                                                onError={e => {
                                                    e.currentTarget.src = Images.thumb;
                                                }}
                                            />
                                        </div>
                                        <div className="overflow-hidden flex-1">
                                            <div className="text-[12px] text-[#B3B4BA] truncate">{product.brand}</div>
                                            <div className="text-[14px] font-bold truncate">{product.name}</div>
                                            <div className="text-[12px] truncate">{product.color}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </section>
            <div className="border-b border-[#F3F3F8]"></div>

            <section className="py-[30px] px-4">
                <div className="font-bold mb-6">컬러맵</div>
                <div className="relative w-full aspect-square">
                    <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 z-0">
                        {Array.from({ length: 36 }).map((_, i) => (
                            <div key={i} className="border border-dashed border-[#F3F3F8]" />
                        ))}
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[calc(100%-70px)] h-px bg-[#D9D9DC] z-10" />
                    <div className="absolute left-1/2 top-1/2 -translate-y-1/2 h-[calc(100%-60px)] w-px bg-[#D9D9DC] z-10" />

                    <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] z-10">맑은</div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-2 text-[12px] z-10">어두운</div>

                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] z-10">탁한</div>
                    <div className="absolute left-1/2 top-2 -translate-x-1/2 text-[12px] z-10">밝은</div>

                    {products.map(product => (
                        <div
                            key={product.id}
                            className="absolute w-[52px] h-[52px] object-contain bg-[#F3F3F8] border-[4px] border-white rounded-md z-20 overflow-hidden py-[7px] px-[6px] shrink-0"
                            style={{
                                left: `${product.x}%`,
                                top: `${product.y}%`,
                                transform: 'translate(-50%, -50%)',
                            }}
                        >
                            <img
                                className="w-full h-full object-contain rounded-[2px]"
                                src={product.imageUrl}
                                alt={product.name}
                                onError={e => {
                                    e.currentTarget.src = Images.thumb;
                                }}
                            />
                        </div>
                    ))}
                </div>
            </section>
            <section className="py-[30px] px-4"></section>
        </div>
    );
};
